// GateViewDetailsModal.tsx - Updated: action buttons in footer matching 3-dot options
import React, { useState } from 'react';
import './GateManagement.css';
import './GateViewDetailsModal.css';
import '../companyManagement/ViewDetailsModal.css';
import { Gate } from './types';

interface GateViewDetailsModalProps {
  gate: Gate;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (gate: Gate) => void;
  onDelete?: (gate: Gate) => void;
}

export const GateViewDetailsModal: React.FC<GateViewDetailsModalProps> = ({
  gate,
  isOpen,
  onClose,
  onEdit,
  onDelete
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  if (!isOpen) return null;

  const handleEditClick = () => {
    onClose();
    onEdit(gate);
  };

  const handleDeleteClick = () => {
    if (onDelete) {
      onClose();
      onDelete(gate);
    }
  };

  return (
    <div className="modal-overlay view-details-overlay" onClick={onClose}>
      <div className="view-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="view-details-header">
          <div className="view-details-header-left" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h2 className="view-details-title">{gate.gateNumber}</h2>
            <span className={`vdm-status-badge ${gate.isActive ? 'active' : 'blacklisted'}`}>
              {gate.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
          <button 
            type="button" 
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close modal"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="view-details-body">
          <div className="view-details-section">
            <h3 className="section-title">Basic Information</h3>
          </div>

          <div className="view-details-grid">
            <div className="view-details-left">
              <div className="detail-field">
                <label className="detail-label">Gate Number</label>
                <div className="detail-value company-name">{gate.gateNumber}</div>
              </div>

              <div className="detail-field">
                <label className="detail-label">Region</label>
                <div className="detail-value phone">{gate.locationName}</div>
              </div>

              <div className="detail-field">
                <label className="detail-label">Status</label>
                <div className="detail-value authorization">
                  {gate.isActive ? 'Active' : 'Inactive'}
                </div>
              </div>
            </div>

            <div className="view-details-right">
              <div className="detail-field">
                <label className="detail-label">Approvers ({gate.approvers?.length || 0})</label>
                <div className="detail-value regions">
                  {gate.approvers && gate.approvers.length > 0 ? (
                    <div className="users-list">
                      {gate.approvers.map((approver) => (
                        <div key={approver.userId} className="user-item-display">
                          <div className="user-main-info">
                            <span className="user-name">{approver.userName}</span>
                          </div>
                          <div className="user-meta-info">
                            <span className="user-email">{approver.userEmail}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    'No approvers assigned'
                  )}
                </div>
              </div>

              <div className="detail-field">
                <label className="detail-label">Reviewers ({gate.reviewers?.length || 0})</label>
                <div className="detail-value regions">
                  {gate.reviewers && gate.reviewers.length > 0 ? (
                    <div className="users-list">
                      {gate.reviewers.map((reviewer) => (
                        <div key={reviewer.userId} className="user-item-display">
                          <div className="user-main-info">
                            <span className="user-name">{reviewer.userName}</span>
                          </div>
                          <div className="user-meta-info">
                            <span className="user-email">{reviewer.userEmail}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    'No reviewers assigned'
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Description Field - Full Width */}
          {gate.description && (
            <div className="view-details-section">
              <div className="detail-field full-width">
                <label className="detail-label">Description</label>
                <div className="detail-value description">
                  {gate.description}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer with action buttons */}
        <div className="view-details-footer" style={{ justifyContent: 'space-between' }}>
          <div className="vdm-actions-left">
            {onDelete && !showDeleteConfirm && (
              <button 
                type="button" 
                className="vdm-action-btn delete"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                </svg>
                Delete
              </button>
            )}
            {showDeleteConfirm && (
              <div className="vdm-delete-confirm">
                <span className="vdm-delete-confirm-text">Delete this gate?</span>
                <button 
                  type="button" 
                  className="vdm-action-btn delete-confirm"
                  onClick={handleDeleteClick}
                >
                  Yes, Delete
                </button>
                <button 
                  type="button" 
                  className="vdm-action-btn cancel-delete"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
          <div className="vdm-actions-right">
            <button 
              type="button" 
              className="vdm-action-btn primary"
              onClick={handleEditClick}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              Edit Gate
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};